import * as Sentry from '@sentry/nextjs';

import type { SoftphoneDiagnostic } from '@poseidon/components/Softphone/softphoneClient.types';

/**
 * Report a softphone call failure to Sentry.
 *
 * The carrier codes that actually explain a broken call (Twilio 31000 TwiML fetch failure, or the
 * instant hangup a rejected `<Dial>` produces in the browser) used to reach `console.error` and
 * nowhere else. Sentry's browser SDK here runs without console capture, so none of it was
 * collected: a production outage in which every outbound call failed ran for five days with no
 * server-side signal at all, and had to be reconstructed from the carrier's own logs by hand.
 *
 * Grouping is by event + code rather than by message, because the SDK's `message` is usually the
 * generic "An unknown error has occurred" — grouping on it would collapse every distinct failure
 * into one issue.
 */
export function reportSoftphoneDiagnostic(diagnostic: SoftphoneDiagnostic): void {
  // A normal hangup after a real conversation is not a fault; only abnormal terminations report.
  if (!diagnostic.abnormal) return;

  const codeLabel = diagnostic.code != null ? ` ${diagnostic.code}` : '';
  const detail = diagnostic.description ?? diagnostic.explanation ?? diagnostic.message ?? 'no carrier detail';
  const error = new Error(`Softphone ${diagnostic.event}${codeLabel}: ${detail}`);
  error.name = 'SoftphoneCallFailure';

  Sentry.captureException(error, {
    tags: {
      softphoneEvent: diagnostic.event,
      // Tagged so "every call failing with one code" is a single filter in Sentry.
      softphoneCode: diagnostic.code != null ? String(diagnostic.code) : 'none',
      softphoneBecameActive: String(diagnostic.becameActive),
    },
    extra: {
      voiceCallId: diagnostic.voiceCallId,
      becameActive: diagnostic.becameActive,
      code: diagnostic.code,
      description: diagnostic.description,
      explanation: diagnostic.explanation,
      carrierMessage: diagnostic.message,
    },
  });
}
